import client from "./client";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface AIStatus {
  configured: boolean;
  model: string;
  embedding_backend?: string;
  copilots?: string[];
}

export interface AITool {
  name: string;
  scope: string;
  description: string;
  required_permission: string | null;
}

export interface ToolTraceEntry {
  tool: string;
  input: Record<string, unknown>;
  output: Record<string, unknown>;
}

export interface CopilotChatResult {
  reply: string;
  trace: ToolTraceEntry[];
  iterations: number;
}

export async function getAIStatus(): Promise<AIStatus> {
  const { data } = await client.get("/ai/status");
  return data;
}

export async function listAITools(): Promise<AITool[]> {
  const { data } = await client.get("/ai/tools");
  return data.tools;
}

export async function sendChat(messages: ChatMessage[]): Promise<string> {
  const { data } = await client.post("/ai/chat", { messages, stream: false });
  return data.reply;
}

export async function sendCopilotChat(
  copilot: string,
  messages: ChatMessage[],
): Promise<CopilotChatResult> {
  const { data } = await client.post(`/ai/copilots/${copilot}/chat`, {
    messages,
  });
  return data;
}

// Copilot metadata for the UI — labels, intros, suggested prompts.
export const COPILOT_META: Record<
  string,
  { label: string; intro: string; suggestions: string[] }
> = {
  general: {
    label: "General",
    intro: "Ask about anything across Lenavio — people, leave, invoices, reports.",
    suggestions: [
      "How many people are on my team?",
      "Summarize open complaints this quarter",
      "What's our current PTO utilization?",
    ],
  },
  employees: {
    label: "People",
    intro: "Search the directory, pull profiles, and answer headcount questions.",
    suggestions: [
      "Find everyone in Engineering",
      "Who reports to Alice?",
      "How many active employees do we have?",
    ],
  },
  leave: {
    label: "Leave",
    intro: "PTO balances, pending approvals, utilization across the org.",
    suggestions: [
      "What's my vacation balance?",
      "Show pending leave requests",
      "Who's out next week?",
    ],
  },
  invoices: {
    label: "Invoicing",
    intro: "Invoice status, outstanding balances, payment totals.",
    suggestions: [
      "Which invoices are overdue?",
      "What's the total invoiced this month?",
      "Show me all draft invoices",
    ],
  },
  reports: {
    label: "Reports",
    intro: "Organization-wide KPIs, breakdowns, and ad-hoc analytics.",
    suggestions: [
      "Give me an org health snapshot",
      "Headcount breakdown by department",
      "What reports can I build?",
    ],
  },
  complaints: {
    label: "Complaints",
    intro: "Case load, severity breakdown, aggregated trends — no identifying details.",
    suggestions: [
      "How many open cases do we have?",
      "Show urgent complaints",
      "Trend of complaints by severity",
    ],
  },
  documents: {
    label: "Docs",
    intro: "Semantic search across uploaded policies and handbooks.",
    suggestions: [
      "What's our parental leave policy?",
      "Find expense reimbursement rules",
      "How many PTO days can I carry over?",
    ],
  },
};
